'use client'

import * as React from 'react'
import { useMemo, useRef, useState } from 'react'
import Link from 'next/link'

import { useAccessibility } from '@/components/a11y/AccessibilityProvider'
import { Button } from '@/components/ui/button'
import { Label } from '@/components/ui/label'
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group'
import { DEFAULT_ACCESSIBILITY_SETTINGS, type AccessibilitySettings } from '@/lib/a11y/types'
import type { Course } from '@/lib/courses/types'
import { deriveSettings } from '@/lib/needs/derive-settings'
import { topRecommendations } from '@/lib/needs/recommend-courses'
import { describeDerivedSettings } from '@/lib/needs/settings-summary'
import { NEEDS_CATEGORY_CARDS } from '@/lib/needs/wizard-content'
import type { NeedsCategory, NeedsProfile } from '@/lib/needs/types'

import { NeedsPreviewStep } from './NeedsPreviewStep'
import { RecommendedCourses } from './RecommendedCourses'

type Step = 'categories' | 'colorblind' | 'preview' | 'done'

type ColorBlindType = 'protanopia' | 'deuteranopia' | 'tritanopia'

const COLOR_BLIND_OPTIONS: { value: ColorBlindType; label: string }[] = [
  { value: 'protanopia', label: 'Teže razlikujem crvenu (protanopija)' },
  { value: 'deuteranopia', label: 'Teže razlikujem zelenu (deuteranopija)' },
  { value: 'tritanopia', label: 'Teže razlikujem plavu (tritanopija)' },
]

interface NeedsWizardProps {
  allCourses: Course[]
}

export function NeedsWizard({ allCourses }: NeedsWizardProps) {
  const { settings, update, announce, authenticated } = useAccessibility()

  const [step, setStep] = useState<Step>('categories')
  const [selected, setSelected] = useState<NeedsCategory[]>([])
  const [colorBlindType, setColorBlindType] = useState<ColorBlindType | undefined>(undefined)

  // Snapshot podešavanja pre nego što live-preview pregazi <html>; "Nazad" ga vraća.
  const snapshotRef = useRef<AccessibilitySettings | null>(null)

  const profile = useMemo<NeedsProfile>(
    () => ({
      categories: selected,
      ...(colorBlindType ? { colorBlindType } : {}),
    }),
    [selected, colorBlindType],
  )

  const derived = useMemo(() => deriveSettings(profile), [profile])
  const summary = useMemo(() => describeDerivedSettings(derived), [derived])
  const recommendations = useMemo(
    () => topRecommendations(profile, allCourses, 3),
    [profile, allCourses],
  )

  function toggleCategory(value: NeedsCategory): void {
    setSelected((prev) =>
      prev.includes(value) ? prev.filter((c) => c !== value) : [...prev, value],
    )
  }

  function enterPreview(): void {
    snapshotRef.current = settings
    update(derived)
    announce('Podešavanja su privremeno primenjena. Pogledaj kako izgleda.')
    setStep('preview')
  }

  function goNextFromCategories(): void {
    if (selected.includes('color-blindness')) {
      setStep('colorblind')
    } else {
      enterPreview()
    }
  }

  function restoreSnapshot(): void {
    if (snapshotRef.current) update(snapshotRef.current)
  }

  function handleBackToCategories(): void {
    restoreSnapshot()
    setStep('categories')
  }

  function handleSave(): void {
    // Podešavanja su već primenjena; samo ih zadržavamo i potvrđujemo.
    snapshotRef.current = null
    announce('Podešavanja su sačuvana.')
    setStep('done')
  }

  function handleSkipAll(): void {
    update(DEFAULT_ACCESSIBILITY_SETTINGS)
    setSelected([])
    setColorBlindType(undefined)
    setStep('done')
  }

  if (step === 'preview') {
    return (
      <NeedsPreviewStep
        summary={summary}
        recommendations={recommendations}
        onBack={
          selected.includes('color-blindness')
            ? () => setStep('colorblind')
            : handleBackToCategories
        }
        onSave={handleSave}
      />
    )
  }

  if (step === 'done') {
    return (
      <div className="flex flex-col gap-8">
        <div className="flex flex-col gap-3">
          <h2 className="text-2xl font-semibold">Sve je spremno 🎉</h2>
          {/* Isti uslov kao u SheetDescription — gostu se podešavanja čuvaju samo
              na ovom uređaju, pa mu ne obećavamo nalog. */}
          <p className="text-muted-foreground">
            Platforma je prilagođena tebi. Podešavanja su sačuvana{' '}
            {authenticated ? 'na tvom nalogu i prate te na svim uređajima.' : 'na ovom uređaju.'}{' '}
            Uvek ih možeš promeniti preko dugmeta „Prikaz i čitanje“ (dole desno).
          </p>
        </div>
        {recommendations.length > 0 && (
          <RecommendedCourses recommendations={recommendations} heading="Kursevi za tebe" />
        )}
        <div className="flex flex-col gap-3 sm:flex-row">
          <Button asChild>
            <Link href="/kursevi">Pogledaj sve kurseve</Link>
          </Button>
          <Button variant="outline" onClick={() => setStep('categories')}>
            Promeni izbor
          </Button>
        </div>
      </div>
    )
  }

  if (step === 'colorblind') {
    return (
      <div className="flex flex-col gap-8">
        <div className="flex flex-col gap-3">
          <h2 className="text-2xl font-semibold">Koje boje teže razlikuješ?</h2>
          <p className="text-muted-foreground">
            Izaberi ono što ti najviše odgovara. Uključićemo filter koji pomaže.
          </p>
        </div>
        <RadioGroup
          value={colorBlindType ?? ''}
          onValueChange={(val) => setColorBlindType(val as ColorBlindType)}
          className="flex flex-col gap-3"
        >
          {COLOR_BLIND_OPTIONS.map((opt) => (
            <div key={opt.value} className="flex items-center gap-3">
              <RadioGroupItem value={opt.value} id={`cb-${opt.value}`} />
              <Label htmlFor={`cb-${opt.value}`}>{opt.label}</Label>
            </div>
          ))}
        </RadioGroup>
        <div className="flex flex-col-reverse gap-3 sm:flex-row sm:justify-between">
          <Button type="button" variant="outline" onClick={() => setStep('categories')}>
            Nazad
          </Button>
          <Button type="button" onClick={enterPreview}>
            Dalje
          </Button>
        </div>
      </div>
    )
  }

  // step === 'categories'
  return (
    <div className="flex flex-col gap-8">
      <div className="flex flex-col gap-3">
        <h2 className="text-2xl font-semibold">Šta ti olakšava korišćenje?</h2>
        <p className="text-muted-foreground">
          Izaberi sve što važi za tebe. Na osnovu toga ćemo automatski podesiti platformu i
          predložiti kurseve. Možeš izabrati više opcija ili preskočiti ovaj korak.
        </p>
      </div>

      <fieldset className="flex flex-col gap-3">
        <legend className="sr-only">Tvoje potrebe</legend>
        <ul className="grid gap-3 sm:grid-cols-2">
          {NEEDS_CATEGORY_CARDS.map((card) => {
            const active = selected.includes(card.value)
            return (
              <li key={card.value}>
                <button
                  type="button"
                  aria-pressed={active}
                  onClick={() => toggleCategory(card.value)}
                  className={`focus-visible:ring-ring flex w-full items-start gap-3 rounded-lg border p-4 text-left transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 ${
                    active
                      ? 'border-primary bg-primary/5 ring-primary ring-1'
                      : 'bg-surface hover:border-primary'
                  }`}
                >
                  <span className="text-3xl" aria-hidden="true">
                    {card.emoji}
                  </span>
                  <span className="flex flex-col gap-0.5">
                    <span className="font-semibold">{card.label}</span>
                    <span className="text-muted-foreground text-sm">{card.description}</span>
                  </span>
                  <span
                    aria-hidden="true"
                    className={`ml-auto mt-1 flex h-5 w-5 shrink-0 items-center justify-center rounded border ${
                      active ? 'border-primary bg-primary text-primary-foreground' : 'border-input'
                    }`}
                  >
                    {active ? '✓' : ''}
                  </span>
                </button>
              </li>
            )
          })}
        </ul>
      </fieldset>

      <div className="flex flex-col-reverse gap-3 sm:flex-row sm:justify-between">
        <Button type="button" variant="ghost" onClick={handleSkipAll}>
          Preskoči ovaj korak
        </Button>
        <Button type="button" onClick={goNextFromCategories}>
          Dalje
        </Button>
      </div>
    </div>
  )
}
